import { useState, useCallback, useEffect } from 'react';

interface GeoPosition {
  lat: number;
  lon: number;
}

// Module-level cache so position persists across components/navigations
let cachedPosition: GeoPosition | null = null;

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function getDistanceKm(
  userLat: number,
  userLon: number,
  targetLat: number,
  targetLon: number
): number {
  return Math.round(haversineKm(userLat, userLon, targetLat, targetLon) * 10) / 10;
}

// Distanza in metri senza arrotondamenti: l'audioguida deve distinguere 30 m da 90 m,
// cosa impossibile con i chilometri arrotondati a 0,1.
export function getDistanceMeters(
  userLat: number,
  userLon: number,
  targetLat: number,
  targetLon: number
): number {
  return haversineKm(userLat, userLon, targetLat, targetLon) * 1000;
}

export function useGeolocation() {
  const [position, setPosition] = useState<GeoPosition | null>(cachedPosition);
  const [loading, setLoading] = useState(false);

  // Sync with cache on mount (in case another component already fetched)
  useEffect(() => {
    if (cachedPosition && !position) {
      setPosition(cachedPosition);
    }
  }, [position]);

  const requestPosition = useCallback(() => {
    if (cachedPosition) {
      setPosition(cachedPosition);
      return;
    }
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        cachedPosition = newPos;
        setPosition(newPos);
        setLoading(false);
      },
      () => setLoading(false),
      { enableHighAccuracy: false, timeout: 10000 }
    );
  }, []);

  return { position, loading, requestPosition };
}
